import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePortal } from './PortalContext';

const PortalJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const { isAuthenticated, fetchJobs, logout } = usePortal();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }

    loadJobs();
  }, [isAuthenticated, navigate]);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchJobs();
      setJobs(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#22c55e';
      case 'in_progress':
        return '#3b82f6';
      case 'scheduled':
        return '#f59e0b';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    if (filter === 'completed') return job.status === 'completed';
    if (filter === 'scheduled') return job.status === 'scheduled' || job.status === 'in_progress';
    return true;
  });

  const completedJobs = jobs.filter(job => job.status === 'completed').length;
  const upcomingJobs = jobs.filter(job => job.status === 'scheduled').length;

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.navContent}>
          <div style={styles.navLeft}>
            <div style={styles.navLogo}>🔧</div>
            <h1 style={styles.navTitle}>Customer Portal</h1>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.content}>
          {/* Breadcrumb */}
          <div style={styles.breadcrumb}>
            <Link to="/portal/dashboard" style={styles.breadcrumbLink}>Dashboard</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <span style={styles.breadcrumbCurrent}>Jobs</span>
          </div>

          {/* Header */}
          <div style={styles.header}>
            <div>
              <h2 style={styles.title}>Job History</h2>
              <p style={styles.subtitle}>
                {jobs.length} total · {completedJobs} completed · {upcomingJobs} upcoming
              </p>
            </div>
          </div>

          {error && (
            <div style={styles.errorBox}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Filter Tabs */}
          <div style={styles.filterTabs}>
            <button
              onClick={() => setFilter('all')}
              style={{
                ...styles.filterTab,
                ...(filter === 'all' ? styles.filterTabActive : {})
              }}
            >
              All Jobs
            </button>
            <button
              onClick={() => setFilter('scheduled')}
              style={{
                ...styles.filterTab,
                ...(filter === 'scheduled' ? styles.filterTabActive : {})
              }}
            >
              Upcoming
            </button>
            <button
              onClick={() => setFilter('completed')}
              style={{
                ...styles.filterTab,
                ...(filter === 'completed' ? styles.filterTabActive : {})
              }}
            >
              Completed
            </button>
          </div>

          {/* Jobs List */}
          <div style={styles.card}>
            {loading ? (
              <div style={styles.loadingState}>
                <div style={styles.spinner}></div>
                <p>Loading jobs...</p>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🔧</div>
                <h3 style={styles.emptyTitle}>No jobs found</h3>
                <p style={styles.emptyText}>
                  {filter !== 'all'
                    ? `No ${filter} jobs found.`
                    : "You don't have any jobs on record yet."}
                </p>
                {filter !== 'all' && (
                  <button
                    onClick={() => setFilter('all')}
                    style={styles.emptyButton}
                  >
                    View all jobs
                  </button>
                )}
              </div>
            ) : (
              <div style={styles.jobsList}>
                {filteredJobs.map((job) => (
                  <div key={job.id} style={styles.jobCard}>
                    <div style={styles.jobHeader}>
                      <div style={styles.jobDate}>
                        <div style={styles.dateIcon}>📅</div>
                        <div>
                          <p style={styles.dateValue}>{formatDate(job.scheduled_date)}</p>
                          {job.scheduled_time && (
                            <p style={styles.timeValue}>{formatTime(job.scheduled_time)}</p>
                          )}
                        </div>
                      </div>
                      <span style={{
                        ...styles.statusBadge,
                        background: `${getStatusColor(job.status)}20`,
                        color: getStatusColor(job.status)
                      }}>
                        {job.status === 'completed' ? '✓' :
                         job.status === 'in_progress' ? '⚡' :
                         job.status === 'scheduled' ? '📅' : '•'} {' '}
                        {job.status}
                      </span>
                    </div>

                    <div style={styles.jobBody}>
                      <h3 style={styles.jobType}>{job.job_type}</h3>
                      <p style={styles.jobAddress}>📍 {job.address}</p>

                      {job.crew_assigned && job.crew_assigned.length > 0 && (
                        <p style={styles.jobCrew}>
                          👤 Technician: {job.crew_assigned.join(', ')}
                        </p>
                      )}

                      {job.actual_hours > 0 && (
                        <p style={styles.jobHours}>
                          ⏱ Duration: {job.actual_hours} hours
                        </p>
                      )}

                      {job.notes && (
                        <div style={styles.jobNotes}>
                          <p style={styles.notesLabel}>Notes:</p>
                          <p style={styles.notesText}>{job.notes}</p>
                        </div>
                      )}
                    </div>

                    {job.status === 'completed' && job.quoted_price > 0 && (
                      <div style={styles.jobFooter}>
                        <p style={styles.priceLabel}>Job Value:</p>
                        <p style={styles.priceValue}>
                          ${parseFloat(job.quoted_price).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Back Link */}
          <Link to="/portal/dashboard" style={styles.backLink}>
            ← Back to Dashboard
          </Link>
        </div>
      </main>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f3f4f6',
    display: 'flex',
    flexDirection: 'column'
  },
  navbar: {
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%'
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  navLogo: {
    fontSize: '24px'
  },
  navTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  logoutButton: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    color: '#6b7280',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  main: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: 'calc(100vh - 73px)',
    padding: '32px 24px'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    maxWidth: '800px'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  },
  breadcrumbLink: {
    color: '#6b7280',
    textDecoration: 'none'
  },
  breadcrumbSeparator: {
    color: '#d1d5db'
  },
  breadcrumbCurrent: {
    color: '#111827',
    fontWeight: '500'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  filterTabs: {
    display: 'flex',
    gap: '8px'
  },
  filterTab: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  filterTabActive: {
    background: 'white',
    color: '#111827',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  loadingState: {
    padding: '60px',
    textAlign: 'center',
    color: '#6b7280'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px'
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '14px',
    margin: '0 0 16px 0'
  },
  emptyButton: {
    color: '#f97316',
    background: 'transparent',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  jobsList: {
    display: 'flex',
    flexDirection: 'column'
  },
  jobCard: {
    padding: '24px',
    borderBottom: '1px solid #f3f4f6',
    ':last-child': {
      borderBottom: 'none'
    }
  },
  jobHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  jobDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  dateIcon: {
    fontSize: '24px'
  },
  dateValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  timeValue: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '2px 0 0 0'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: '500'
  },
  jobBody: {
    marginBottom: '16px'
  },
  jobType: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  jobAddress: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 8px 0'
  },
  jobCrew: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 4px 0'
  },
  jobHours: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 8px 0'
  },
  jobNotes: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '12px'
  },
  notesLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#9ca3af',
    margin: '0 0 4px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  notesText: {
    fontSize: '14px',
    color: '#374151',
    margin: 0,
    lineHeight: '1.5'
  },
  jobFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '16px',
    borderTop: '1px solid #f3f4f6'
  },
  priceLabel: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  priceValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  },
  backLink: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500'
  }
};

export default PortalJobs;
